import { PresentationMetrics, presentationDomains } from "./presentation-metrics.js";
import { NetworkHistory } from "./network-history.js";
import { GpuTimer } from "./gpu-timer.js";

// Bounded storage; sampling never allocates or touches the DOM. The graph uses
// the renderer's frame loop, at most 10 Hz; text/statistics update at 4 Hz.
export class FrameHistory {
    constructor(capacity = 240) {
        this.frames = new Float32Array(capacity);
        this.cpu = new Float32Array(capacity);
        this.gpu = new Float32Array(capacity);
        this.timestamps = new Float64Array(capacity);
        this.sorted = new Float32Array(capacity);
        this.reset();
    }
    reset() { this.count = this.cursor = 0; this.previous = null; this.timestamps.fill(NaN); this.clearGpu(); }
    clearGpu() { this.gpu.fill(NaN); }
    recordGpu(now, ms) {
        // Match delayed results to their original frame; ignore overwritten or
        // reset frames, and never count an unavailable sample as zero GPU work.
        const index = this.timestamps.indexOf(now);
        if (index >= 0) this.gpu[index] = ms;
    }
    push(now, cpu) {
        const previous = this.previous; this.previous = now;
        if (previous === null || now <= previous) return;
        this.frames[this.cursor] = now - previous; this.cpu[this.cursor] = cpu;
        this.timestamps[this.cursor] = now; this.gpu[this.cursor] = NaN;
        this.cursor = (this.cursor + 1) % this.frames.length;
        this.count = Math.min(this.count + 1, this.frames.length);
    }
    statistics() {
        if (!this.count) return null;
        let sum = 0, cpu = 0, slow = 0, gpu = 0, gpuFrames = 0, gpuCount = 0;
        for (let i = 0; i < this.count; i++) {
            const value = this.frames[i]; sum += value; cpu += this.cpu[i];
            slow += value > 33.34; this.sorted[i] = value;
            if (Number.isFinite(this.gpu[i])) { gpu += this.gpu[i]; gpuFrames += value; gpuCount++; }
        }
        const values = this.sorted.subarray(0, this.count); values.sort();
        return { fps: 1000 * this.count / sum, mean: sum / this.count,
            p95: values[Math.ceil(this.count * .95) - 1], max: values[this.count - 1],
            cpu: cpu / this.count, cpuPercent: cpu / sum * 100, slow,
            gpu: gpuCount ? gpu / gpuCount : null, gpuPercent: gpuCount ? gpu / gpuFrames * 100 : null };
    }
}

export class PerformanceOverlay {
    constructor(view, root = document) {
        this.view = view;
        this.button = root.getElementById('performance-toggle');
        this.panel = root.getElementById('performance-overlay');
        this.toolbar = root.getElementById('toolbar');
        this.fps = root.getElementById('toolbar-fps');
        this.fpsStart = null; this.fpsFrames = 0;
        this.more = root.getElementById('performance-more');
        this.detailsPanel = root.getElementById('performance-details');
        this.domainsRoot = root.getElementById('performance-domains');
        this.onMore = () => {
            if (this.more.checked) this.createDetails();
            this.panel.classList.toggle('is-expanded', this.more.checked);
            this.detailsPanel.hidden = !this.more.checked;
            this.reset(); this.layout();
        };
        this.more.addEventListener('change', this.onMore);
        this.graph = root.getElementById('performance-graph');
        this.context = this.graph.getContext('2d', { alpha: false });
        this.networkGraph = root.getElementById('performance-network-graph');
        this.networkContext = this.networkGraph?.getContext('2d', { alpha: false });
        this.network = new NetworkHistory();
        this.fields = Object.fromEntries([...this.panel.querySelectorAll('[data-perf]')].map(node => [node.dataset.perf, node]));
        this.history = new FrameHistory(); this.enabled = false;
        this.toggle = () => this.setEnabled(!this.enabled);
        this.onVisibility = () => this.reset();
        this.onContextChange = () => { this.reset(); this.gpuTimer = null; };
        view.canvas.addEventListener('webglcontextlost', this.onContextChange);
        view.canvas.addEventListener('webglcontextrestored', this.onContextChange);
        this.button.addEventListener('click', this.toggle);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.resize = new ResizeObserver(() => this.layout());
        this.button.disabled = false;
    }
    get details() { return this.enabled && this.more.checked && !document.hidden ? this.presentation : null; }
    tickFps(now) {
        if (!this.fps) return;
        if (document.hidden || this.fpsStart === null || now < this.fpsStart || now - (this.fpsPrevious ?? now) > 2000) {
            this.fpsStart = now; this.fpsFrames = 0; this.fps.textContent = '— FPS';
        } else {
            this.fpsFrames++;
            if (now - this.fpsStart >= 500) {
                this.fps.textContent = `${Math.round(this.fpsFrames * 1000 / (now - this.fpsStart))} FPS`;
                this.fpsStart = now; this.fpsFrames = 0;
            }
        }
        this.fpsPrevious = now;
    }
    reset() { this.gpuTimer?.reset(); this.history.reset(); this.network.reset(); this.presentation?.reset(); this.lastText = this.lastGraph = -Infinity; this.syncAudioMetrics(); }
    beginGpu(now) {
        if (!this.enabled || document.hidden || this.view.renderer.getContext().isContextLost()) return;
        this.gpuTimer ??= new GpuTimer(this.view.renderer.getContext(),
            (time, ms) => this.history.recordGpu(time, ms), () => this.history.clearGpu());
        this.gpuTimer.begin(now);
    }
    endGpu() { this.gpuTimer?.end(); }
    syncAudioMetrics() { this.view.presentationAudio?.setMetricsEnabled(!!this.details); }
    createDetails() {
        if (this.presentation) return;
        this.presentation = new PresentationMetrics();
        this.rows = new Map();
        const fragment = document.createDocumentFragment();
        for (const [key, label, limit] of presentationDomains) {
            if (key === 'camera' || key === 'liveFrame') {
                const heading = document.createElement('tr'), title = document.createElement('th');
                title.colSpan = 5; title.scope = 'colgroup';
                title.textContent = key === 'camera' ? 'Rendering & presentation' : 'Live-only work';
                heading.append(title); fragment.append(heading);
            }
            const row = document.createElement('tr'); row.dataset.domain = key;
            const name = document.createElement('td'), title = document.createElement('strong'); title.textContent = label;
            const quota = document.createElement('small'); quota.textContent = limit;
            const gauge = document.createElement('small'); name.append(title, quota, gauge); row.append(name);
            const fields = [];
            for (let i = 0; i < 3; i++) { const cell = document.createElement('td'); cell.textContent = '—'; fields.push(cell); row.append(cell); }
            const plot = document.createElement('td'), graph = document.createElement('canvas');
            plot.className = 'live-performance-plot';
            const scale = document.createElement('small'); scale.className = 'live-performance-scale';
            graph.className = 'live-performance-spark'; graph.width = 120; graph.height = 28;
            graph.setAttribute('role', 'img'); graph.setAttribute('aria-label', `${label}: CPU utilization and update frequency over the last 12 seconds`);
            plot.append(graph, scale); row.append(plot); fragment.append(row);
            this.rows.set(key, { fields, gauge, graph, scale, context: graph.getContext('2d', { alpha: false }) });
        }
        this.domainsRoot.append(fragment);
    }
    setEnabled(enabled) {
        this.enabled = enabled; this.panel.hidden = !enabled;
        this.button.setAttribute('aria-pressed', String(enabled));
        this.button.setAttribute('aria-expanded', String(enabled));
        this.reset();
        if (enabled) {
            this.resize.observe(this.toolbar); this.resize.observe(this.panel); this.resize.observe(this.view.canvas);
            this.layout();
        } else this.resize.disconnect();
    }
    layout() {
        if (!this.enabled) return;
        this.panel.style.top = `${this.toolbar.offsetTop + this.toolbar.offsetHeight + 8}px`;
        // One backing pixel per CSS pixel is sufficient for a diagnostic chart.
        const width = Math.max(1, this.graph.clientWidth), height = Math.max(1, this.graph.clientHeight);
        if (this.graph.width !== width) this.graph.width = width;
        if (this.graph.height !== height) this.graph.height = height;
        this.lastGraph = -Infinity;
    }
    sample(now, start, updateEnd, waterEnd, end) {
        if (!this.enabled || document.hidden) return;
        const details = this.details, measured = details ? performance.now() : 0;
        this.history.push(now, end - start);
        this.network.push(this.view.liveMetrics?.worker);
        if (now - this.lastText >= 250) {
            this.lastText = now;
            this.refresh(updateEnd - start, waterEnd - updateEnd, end - waterEnd);
            if (details) this.refreshDetails(measured);
        }
        if (now - this.lastGraph >= 100) { this.lastGraph = now; this.draw(); this.drawNetwork(); if (details) this.drawDetails(measured); }
        details?.record('overlay', performance.now() - measured);
    }
    refresh(update, water, draw) {
        const stats = this.history.statistics(), view = this.view, info = view.renderer.info;
        const set = (name, value) => { if (this.fields[name].textContent !== value) this.fields[name].textContent = value; };
        const ms = value => `${value.toFixed(1)} ms`;
        set('fps', stats ? stats.fps.toFixed(0) : '—');
        set('frame', stats ? ms(stats.mean) : '—');
        set('tail', stats ? `p95 ${ms(stats.p95)} · max ${ms(stats.max)} · >33 ms: ${stats.slow}` : 'Collecting frames…');
        set('cpu', stats ? `${ms(stats.cpu)} (${stats.cpuPercent.toFixed(1)}%)` : '—');
        set('gpu', this.gpuTimer?.supported === false ? `Unavailable — ${this.gpuTimer.unavailableReason}` : stats?.gpu != null
            ? this.gpuTimer?.measurement === 'completion' ? `≤ ${ms(stats.gpu)} completion (includes queue/poll delay)` : `${ms(stats.gpu)} (${stats.gpuPercent.toFixed(1)}%)` : '—');
        set('stages', `Latest CPU: update ${ms(update)} · water ${ms(water)} · draw ${ms(draw)}`);
        set('calls', info.render.calls.toLocaleString());
        set('triangles', info.render.triangles.toLocaleString());
        set('resources', `${info.memory.geometries} geometries · ${info.memory.textures} textures · ${info.programs?.length ?? 0} programs`);
        let admitted = 0;
        for (const item of view.meshes) if (item.mesh.visible && item.mesh.layers.test(view.camera.layers)) admitted++;
        set('scene', `PVS batches ${admitted}/${view.meshes.length} · entities ${view.liveScene?.entities.size ?? 0} · queued assets ${view.assetTasks.length}`);
        set('display', `${view.canvas.width} × ${view.canvas.height} · ${view.renderer.getPixelRatio()}× · ${view.loading ? 'loading (draws throttled)' : view.assetsLoading ? 'loading assets' : 'ready'}`);
    }
    drawNetwork() {
        const ctx = this.networkContext; if (!ctx) return;
        const graph = this.networkGraph, h = this.network, report = this.view.liveMetrics?.worker;
        const graphWidth = Math.max(1, graph.clientWidth);
        if (graph.width !== graphWidth) graph.width = graphWidth;
        if (graph.height !== 80) graph.height = 80;
        const width = graph.width, height = graph.height, peak = Math.max(1024, ...h.incoming, ...h.outgoing);
        ctx.fillStyle = '#10171e'; ctx.fillRect(0, 0, width, height);
        const oldest = (h.cursor - h.count + h.incoming.length) % h.incoming.length;
        for (const [values, color] of [[h.incoming, '#62dfbc'], [h.outgoing, '#edba70']]) {
            ctx.strokeStyle = color; ctx.beginPath();
            for (let i = 0; i < h.count; i++) {
                const x = width * i / (h.incoming.length - 1), y = 60 * (1 - values[(oldest + i) % values.length] / peak);
                if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
            }
            ctx.stroke();
        }
        ctx.fillStyle = '#b5c0ca'; ctx.font = '10px monospace';
        ctx.fillText(`0–${(peak / 1024).toFixed(1)} KiB/s · Live WebSocket payload · up to 120 samples`, 4, 76);
        const [incoming, outgoing] = h.latest();
        const field = this.fields.network;
        if (field) {
            const pressure = report && (report.bufferedAmount >= report.sendBudget * .8 || report.voiceInFlight >= report.voiceBudget * .8);
            field.textContent = report ? `↓ ${(incoming / 1024).toFixed(1)} KiB/s · ↑ ${(outgoing / 1024).toFixed(1)} KiB/s · send queue ${report.bufferedAmount}/${report.sendBudget} B · voice delivery ${report.voiceInFlight}/${report.voiceBudget} · voice drops ${report.voiceDropped} · resyncs ${report.resyncs}${pressure ? ' · Approaching browser queue budget' : ''}` : 'Live network metrics available while watching a server.';
        }
    }
    draw() {
        const ctx = this.context; if (!ctx) return;
        const { width, height } = this.graph, h = this.history;
        ctx.fillStyle = '#10171e'; ctx.fillRect(0, 0, width, height);
        let peak = 50;
        for (let i = 0; i < h.count; i++) peak = Math.max(peak, h.frames[i], h.cpu[i], Number.isFinite(h.gpu[i]) ? h.gpu[i] : 0);
        const scale = Math.ceil(peak / 10) * 10, plotHeight = Math.max(1, height - 16);
        ctx.font = '10px monospace'; ctx.lineWidth = 1;
        for (const budget of [16.67, 33.33]) {
            const y = plotHeight * (1 - budget / scale);
            ctx.strokeStyle = '#3b4651'; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(width, y); ctx.stroke();
            ctx.fillStyle = '#b5c0ca'; ctx.fillText(`${budget.toFixed(1)} ms`, 4, Math.max(10, y - 3));
        }
        const oldest = (h.cursor - h.count + h.frames.length) % h.frames.length;
        for (const [values, color] of [[h.frames, '#62dfbc'], [h.cpu, '#edba70'], [h.gpu, '#a99bff']]) {
            ctx.strokeStyle = color; ctx.beginPath();
            let connected = false;
            for (let i = 0; i < h.count; i++) {
                const value = values[(oldest + i) % h.frames.length];
                if (!Number.isFinite(value)) { connected = false; continue; }
                const x = width * (h.frames.length - h.count + i) / (h.frames.length - 1);
                const y = Math.max(0, plotHeight * (1 - value / scale));
                if (connected) ctx.lineTo(x, y); else ctx.moveTo(x, y);
                connected = true;
            }
            ctx.stroke();
        }
        ctx.fillStyle = '#b5c0ca'; ctx.fillText(`0–${scale} ms · last ${h.count} rendered frames`, 4, height - 3);
    }
    refreshDetails(now) {
        const put = (node, text) => { if (node.textContent !== text) node.textContent = text; };
        for (const [key, row] of this.rows) {
            const s = this.presentation.summary(key, now);
            put(row.fields[0], s.mean === null ? '—' : `${s.mean.toFixed(2)} / ${s.p95.toFixed(2)} / ${s.max.toFixed(2)}`);
            put(row.fields[1], s.hz.toFixed(1)); put(row.fields[2], s.percent.toFixed(1));
        }
        const view = this.view, scene = view.liveScene, audio = view.presentationAudio;
        let animated = 0, sprites = 0, ragdolls = 0, trails = 0, points = 0, pending = 0, ambient = 0, voices = 0;
        for (const entity of scene?.entities.values() ?? []) {
            animated += !!entity.animation; sprites += !!entity.sprite; ragdolls += !!entity.ragdoll;
            pending += !!entity.pending; ambient += !!entity.ambient;
            if (entity.sprite?.trail) { trails++; points += entity.sprite.trail.points.length; }
        }
        for (const entry of scene?.sounds.entries.values() ?? []) voices += !!entry.media && !entry.media.paused;
        const particles = scene?.particles ?? view.mapParticles;
        const gauges = {
            camera: 'Local input · latest rendered camera only',
            sprays: `${scene?.sprays.entries.size ?? 0} retained sprays`,
            styles: 'Map lightstyle animation',
            movement: `${scene?.entities.size ?? 0} entities · 200 ms presentation delay`,
            animation: `${animated} animated models · ${scene?.clips.entries.size ?? 0} cached clips`,
            sprites: `${sprites} sprites · ${trails} trails / ${points} points · ${scene?.sprays.entries.size ?? 0} sprays`,
            particles: `${particles?.effects.size ?? 0} active + ${particles?.draining.size ?? 0} draining systems · ${particles?.nodeCount ?? 0}/256 nodes · ${particles?.capacity ?? 0}/16384 particles reserved · ${particles?.tracesUsed ?? 0}/256 traces`,
            physics: `${ragdolls} ragdolls · ${scene?.gibs.size ?? 0} gibs/karts · ${scene?.deathTracesUsed ?? 0}/256 traces`,
            lighting: `${view.lighting?.dynamic.length ?? 0}/32 dynamic lights · ${ambient} ambient recipients · ${scene?.ambientSamples ?? 0}/8 sampled this frame`,
            models: `${scene?.running ?? 0}/6 loads · ${pending} pending models · ${view.assetTasks.length} map assets queued`,
            sound: `${voices}/128 playing · ${scene?.sounds.running ?? 0}/4 loads · ${scene?.sounds.entries.size ?? 0} sound records`,
            voice: `${audio?.receivedPackets ?? 0} packets · ${audio?.decoder?.decodeQueueSize ?? 0}/8 decode queue · ${audio?.bufferMetrics ? `${audio.bufferMetrics.bufferMs.toFixed(0)}/200 ms buffer · ${audio.bufferMetrics.underruns} underruns · ${audio.bufferMetrics.overflows} overflows` : 'audio buffer not active'}`,
            hud: 'Event-driven HUD work and visible timer callbacks',
            visibility: `${view.meshes.length} map batches`,
            transforms: 'World and sky hierarchy updates, before water submission',
            apply: `${scene?.dictionary.size ?? 0} dictionary entries`,
            water: 'Includes reflections/refractions on the main thread',
            draw: 'Driver submission and scene traversal; excludes GPU execution',
            overlay: 'Instrumentation/graphs are disabled when More metrics is off',
            liveFrame: scene ? `${scene.entities.size} entities · inclusive total, do not add to the detailed rows` : 'Available while watching a live server',
            liveVisibility: scene ? `${scene.entities.size} entities · ${scene.gibs.size} gibs/karts` : 'Available while watching a live server',
            liveCallbacks: scene ? 'Live page integration outside the scene simulation' : 'Available while watching a live server',
        };
        const disabled = 'Disabled · graph retains up to 12 s of earlier CPU work';
        if (view.debugOptions?.renderParticles === false) gauges.particles = disabled;
        if (view.debugOptions?.renderSprays === false) gauges.sprays = disabled;
        if (view.debugOptions?.renderLiveEntities === false)
            for (const key of ['movement', 'animation', 'sprites', 'physics', 'models', 'lighting']) gauges[key] = disabled;
        if (view.debugOptions?.renderWorld === false) gauges.water = disabled;
        for (const [key, text] of Object.entries(gauges)) put(this.rows.get(key).gauge, text);
        const report = view.liveMetrics;
        const ms = value => Number.isFinite(value) ? `${value.toFixed(2)} ms` : '—';
        put(this.fields.pipeline, `Worker frame p95 ${ms(report?.worker?.timings.frameMs.p95)} · worker → main delivery p95 ${ms(report?.delivery?.deliveryMs.p95)} · main-thread timings below exclude native audio decoding and GPU execution.`);
    }
    drawDetails(now) {
        const metrics = this.presentation; metrics.advance(now);
        for (const [key, row] of this.rows) {
            const ctx = row.context; if (!ctx) continue;
            const s = metrics.series.get(key), width = row.graph.width, height = row.graph.height;
            ctx.fillStyle = '#10171e'; ctx.fillRect(0, 0, width, height);
            let scale = 5;
            for (let i = 0; i < metrics.capacity; i++) scale = Math.max(scale, s.cpu[i]);
            scale = Math.ceil(scale / 5) * 5;
            // A bucket is 100 ms, so milliseconds/bucket numerically equals CPU %.
            ctx.strokeStyle = '#354451'; ctx.beginPath();
            const reference = 1;
            ctx.moveTo(0, reference); ctx.lineTo(width, reference); ctx.stroke();
            ctx.strokeStyle = '#edba70'; ctx.beginPath();
            for (let i = 0; i < metrics.capacity; i++) {
                const bucket = metrics.bucket - metrics.capacity + 1 + i;
                const value = bucket < 0 ? 0 : s.cpu[bucket % metrics.capacity];
                const x = i * (width - 1) / (metrics.capacity - 1), y = height - 1 - value / scale * (height - 2);
                if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
            }
            ctx.stroke();
            let hzScale = 60;
            for (let i = 0; i < metrics.capacity; i++) hzScale = Math.max(hzScale, s.calls[i] * 10);
            hzScale = Math.ceil(hzScale / 10) * 10;
            ctx.strokeStyle = '#62dfbc'; ctx.beginPath();
            for (let i = 0; i < metrics.capacity; i++) {
                const bucket = metrics.bucket - metrics.capacity + 1 + i;
                const hz = bucket < 0 ? 0 : s.calls[bucket % metrics.capacity] * 10;
                const x = i * (width - 1) / (metrics.capacity - 1), y = height - 1 - hz / hzScale * (height - 2);
                if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
            }
            ctx.stroke();
            const label = `0–${scale}% · 0–${hzScale} Hz`;
            if (row.scale.textContent !== label) row.scale.textContent = label;
        }
    }
    dispose() {
        this.setEnabled(false); this.resize.disconnect();
        this.button.removeEventListener('click', this.toggle);
        this.more.removeEventListener('change', this.onMore);
        document.removeEventListener('visibilitychange', this.onVisibility);
        this.view.canvas.removeEventListener('webglcontextlost', this.onContextChange);
        this.view.canvas.removeEventListener('webglcontextrestored', this.onContextChange);
    }
}

export function bindPerformanceOverlay(view, root = document) {
    if (view && root.getElementById('performance-overlay')) view.performanceOverlay = new PerformanceOverlay(view, root);
}
